<script setup lang="ts">
import type { OutputBlockData, OutputData } from '@editorjs/editorjs';
import type { EventScheduleItemDTO } from '~/shared/api/client/events/events.dto';
import {
  CLIENT_API_EVENT_SCHEDULE_URL,
  CLIENT_API_EVENT_SCHEDULE_KEY,
  CLIENT_EVENTS_URL,
} from '~/shared/api/client/events/events.const';
import { useCustomFetch } from '~/shared/lib/useCustomFetch';
import { useEventsScheduleCollectionSeo } from '../../lib/seo';
import { parseLocalDateTime } from '~/shared/utils/parseLocalDateTime';
import { getMonthName } from '~/shared/utils/formatDate';
import { ChronicleBanner2 } from '~/entities/chronicle_banners';
import { EventsCarousel } from '~/widgets/events-carousel';
import ArticleParser from '~/entities/article/ui/parser/ArticleParser.vue';

const currentYear = new Date().getFullYear();

const config = useRuntimeConfig();

const { data, status } = useCustomFetch<EventScheduleItemDTO[]>(
  CLIENT_API_EVENT_SCHEDULE_URL,
  { key: CLIENT_API_EVENT_SCHEDULE_KEY, query: { year: currentYear } },
);

function tryParseDescription(description: string | null): OutputData | null {
  try {
    const parsed = description ? (JSON.parse(description) as OutputData) : null;
    return Array.isArray(parsed?.blocks) ? parsed : null;
  } catch {
    return null;
  }
}

function formatScheduleDate(
  startIso: string,
  endIso: string,
  monthOnly: boolean,
): string {
  const start = parseLocalDateTime(startIso);
  const end = parseLocalDateTime(endIso);

  if (monthOnly) {
    return getMonthName(start.month - 1, 'nominative').toLowerCase();
  }

  const startDay = start.day;
  const startMonth = getMonthName(start.month - 1, 'genitive').toLowerCase();

  if (
    start.year === end.year &&
    start.month === end.month &&
    start.day === end.day
  ) {
    return `${startDay} ${startMonth}`;
  }

  const endDay = end.day;
  const endMonth = getMonthName(end.month - 1, 'genitive').toLowerCase();

  if (start.month === end.month && start.year === end.year) {
    return `${startDay}–${endDay} ${endMonth}`;
  }

  return `${startDay} ${startMonth} – ${endDay} ${endMonth}`;
}

const items = computed(() => {
  if (!data.value) {
    return [];
  }
  return data.value
    .filter((item) => item.status === 'active')
    .sort((a, b) => a.start_time.localeCompare(b.start_time))
    .map((item) => ({
      ...item,
      formattedDate: formatScheduleDate(
        item.start_time,
        item.end_time,
        item.month_only,
      ),
      parsedDescription: tryParseDescription(item.description),
    }));
});

useEventsScheduleCollectionSeo(items);
</script>

<template>
  <div :class="$style.root">
    <div :class="$style.content">
      <h2 :class="$style.title">График мероприятий {{ currentYear }}</h2>

      <div v-if="status === 'pending'" :class="$style.loader">
        <Loader />
      </div>

      <template v-else-if="items.length > 0">
        <div :class="$style.table">
          <div :class="$style.header">
            <span :class="$style.header__date">Дата</span>
            <span :class="$style.header__name">Мероприятие</span>
            <span :class="$style.header__desc">Описание</span>
          </div>

          <div v-for="item in items" :key="item.id" :class="$style.row">
            <span :class="$style.row__date">{{ item.formattedDate }}</span>
            <div :class="$style.row__content">
              <NuxtLink
                :to="`${CLIENT_EVENTS_URL}${item.id}`"
                :class="$style.row__name"
              >
                {{ item.name }}
              </NuxtLink>
              <div v-if="item.description" :class="$style.row__desc">
                <ArticleParser
                  v-if="item.parsedDescription"
                  :blocks="item.parsedDescription.blocks as OutputBlockData[]"
                />
                <div
                  v-else
                  :class="$style.row__descPlain"
                  v-html="item.description"
                />
              </div>
            </div>
          </div>
        </div>

        <a
          :href="`${config.public.API_SERVER_URL}/events/calendar/pdf?year=${currentYear}`"
          target="_blank"
          :class="$style.download"
        >
          <Icon
            name="my-icon:i-download"
            size="24"
            :class="$style.download__icon"
          />
          <span>Скачать PDF</span>
        </a>
      </template>

      <div v-else-if="status === 'error'" :class="$style.empty">
        <p :class="$style.empty__text">Не удалось загрузить график</p>
      </div>

      <div v-else :class="$style.empty">
        <p :class="$style.empty__text">Нет запланированных мероприятий</p>
      </div>
    </div>
    <ChronicleBanner2 :class="$style.chronicleBanner" />
    <EventsCarousel v-if="status !== 'pending'" />
  </div>
</template>

<style module lang="scss">
.root {
  display: flex;
  flex-direction: column;
}

.root .chronicleBanner {
  border-bottom: none;
}

.content {
  display: flex;
  flex-direction: column;
  gap: var(--space-3x);
  padding: 16px 20px 0;

  @media (min-width: 1380px) {
    padding-left: 110px;
    padding-right: 110px;
  }
}

.title {
  @include text_h3;
  color: var(--color-black-1);
  margin: 0;
}

.loader {
  display: flex;
  justify-content: center;
  padding: 60px 0;
}

.table {
  display: flex;
  flex-direction: column;
}

.header {
  display: flex;
  gap: var(--space-1-5x);
  padding: 10px 20px;
  background-color: var(--color-gray-bg);
  border: 1px solid var(--color-gray-line);
}

.header__date,
.header__name,
.header__desc {
  @include text_caption2_caps;
  color: var(--color-black-1);
}

.header__date {
  width: 97px;
  flex-shrink: 0;
}

.header__name {
  width: 234px;
  flex-shrink: 0;
}

.header__desc {
  flex: 1;
  min-width: 0;
}

.row {
  display: flex;
  gap: var(--space-1-5x);
  padding: 20px;
  border: 1px solid var(--color-gray-line);
  border-top: none;
  align-items: flex-start;
}

.row__date {
  @include text_body2;
  color: var(--color-black-1);
  width: 97px;
  flex-shrink: 0;
}

.row__content {
  display: contents;
}

.row__name {
  @include text_body1_large_medium;
  color: var(--color-black-1);
  width: 234px;
  flex-shrink: 0;
  text-decoration: none;
  transition: color 0.2s;

  &:hover {
    color: var(--color-red-1);
  }
}

.row__desc {
  @include text_body1;
  color: var(--color-black-1);
  line-height: 130%;
  flex: 1;
  min-width: 0;

  p {
    margin: 0 0 8px;

    &:last-child {
      margin-bottom: 0;
    }
  }

  a {
    color: var(--color-red-1);
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }
}

.row__descPlain {
  white-space: pre-line;
}

.download {
  display: inline-flex;
  align-items: center;
  align-self: flex-start;
  gap: var(--space-1-5x);
  text-decoration: none;
  cursor: pointer;

  @include text_caption1_caps;
  color: var(--color-red-1);
  transition: opacity 0.2s;

  &:hover {
    opacity: 0.7;
  }
}

.download__icon {
  color: var(--color-gray-text);
}

.empty {
  display: flex;
  justify-content: center;
  padding: 60px 0;
}

.empty__text {
  @include text_body1;
  color: var(--color-gray-text);
}

@media (max-width: 1379px) {
  .title {
    font-size: 32px;
  }
}

@media (max-width: 1023px) {
  .header__name,
  .row__name {
    width: 180px;
  }
}

@media (max-width: 599px) {
  .title {
    font-size: 24px;
  }

  .row__name {
    font-size: 14px;
  }

  .row__desc {
    font-size: 14px;
  }
}

@media (max-width: 479px) {
  .header__date,
  .header__name,
  .row__date,
  .row__desc {
    font-size: 9px;
  }

  .row__name {
    font-size: 12px;
  }

  .header__desc {
    display: none;
  }

  .header__name {
    width: auto;
    flex: 1;
    min-width: 0;
  }

  .row__content {
    display: flex;
    flex-direction: column;
    gap: var(--space-0-5x);
    flex: 1;
    min-width: 0;
  }

  .row__name {
    width: auto;
  }
}
</style>
